import Cookies from "js-cookie";
import { markRaw } from "vue";

const MAX_QUICK_RETRIES = 5;
const MAX_RETRIES = 20;

function getConnectionUrl() {
  const baseUrl = import.meta.env.VITE_API_URL.replace("https://", "wss://")
    .replace("http://", "ws://")
    .replace(":8081", ":8083");
  const url = new URL(baseUrl);
  url.pathname += "notifications";

  // websockets should only be used after being authenticated
  const accessToken = Cookies.get("access_token");
  const invalidAccessTokens = [null, undefined, "null", "undefined"];
  if (invalidAccessTokens.indexOf(accessToken) > -1) return;
  url.searchParams.set("Access-Token", accessToken);

  const impersonating = Cookies.get("impersonating");
  if (impersonating) {
    url.searchParams.set("Impersonating", impersonating);
  }

  return url.toString();
}

class TunnlWebSockets {
  retryCount = 0;
  retryInterval = 1000;

  constructor(app) {
    if (!window["WebSocket"]) {
      console.warn("browser does not support WebSocket protocol");
      return;
    }
    const _tunnlWebSockets = this;
    this._channels = {};
    app.mixin({
      beforeCreate() {
        this.$ws = _tunnlWebSockets;
      },
      created() {
        const channels = this.channels || this.$options.channels;
        if (!channels) {
          return;
        }

        for (let [name, channel] of Object.entries(channels)) {
          if (!_tunnlWebSockets._channels[name]) {
            _tunnlWebSockets._channels[name] = [];
          }
          _tunnlWebSockets._channels[name].push(channel.received.bind(this));
        }
      },
    });
  }

  initialize(subscriptions = new Object()) {
    this._subscriptions = subscriptions;
    this._messageQueue = [];
    this._setupConnection();
    for (let [channel, taskIds] of Object.entries(this._subscriptions)) {
      for (let taskId of taskIds) {
        this.sendMessage({
          action: "subscribe",
          payload: {
            channel,
            id: taskId,
          },
        });
      }
    }
  }

  _setupConnection() {
    if (this._connection) {
      console.warn(
        "there's an active websocket. skipping setting up connection",
      );
      return;
    }
    const url = getConnectionUrl();
    if (!url) return;
    this._connection = new WebSocket(url);
    this._connection.onopen = this._onOpen.bind(this);
    this._connection.onmessage = this._onMessage.bind(this);
    this._connection.onclose = this._onClose.bind(this);
    this._connection.onerror = this._onError.bind(this);
  }

  _onOpen() {
    this.retryCount = 0;
    this.retryInterval = 1000;
    while (this._messageQueue.length) {
      const message = this._messageQueue.shift();
      const payload = JSON.stringify(message);
      this._connection.send(payload);
    }
  }

  _onClose() {
    this._connection = null;
    if (this.retryCount < MAX_RETRIES) {
      setTimeout(() => {
        this.initialize(this._subscriptions);
      }, this.retryInterval);
      this.retryCount++;
      if (this.retryCount > MAX_QUICK_RETRIES) {
        this.retryInterval *= 2;
      }
    }
  }

  _onMessage(evt) {
    if (!evt.data) {
      console.warn("no data found in event", evt);
      return;
    }
    const data = JSON.parse(evt.data);
    if (data.ping) {
      return;
    }
    if (!(data.channel && data.payload)) {
      console.warn("no channel found within data payload", data);
      return;
    }
    if (!(data.channel in this._subscriptions)) {
      console.warn(`not subscribed to channel = ${data.channel}`);
      return;
    }
    if (!this._channels[data.channel]) {
      console.warn(`channel ${data.channel} does not exist`);
      return;
    }
    this._channels[data.channel].forEach((fct) => {
      if (data.channel == "NotificationChannel") {
        return fct(data.payload);
      }
      if (data.channel in this._subscriptions) {
        if ("id" in data.payload) {
          const taskIdIndex = this._subscriptions[data.channel].indexOf(
            data.payload.id,
          );
          if (taskIdIndex > -1) {
            this._subscriptions[data.channel].splice(taskIdIndex, 1);
          } else {
            console.warn(
              `unable to find subscription for taskId = ${data.payload.id}`,
            );
          }
        } else {
          console.warn(`no taskId detected for : ${data.payload}`);
        }
      } else {
        console.warn(`invalid channel detected: ${data.channel}`);
      }
      fct(data.payload);
    });
  }

  _onError() {}

  sendMessage(data) {
    if (this._connection?.readyState !== 1) {
      this._messageQueue.push(data);
      return;
    }
    const payload = JSON.stringify(data);
    this._connection.send(payload);
  }

  subscribe({ channel, payload } = {}) {
    if (!(channel in this._subscriptions)) {
      this._subscriptions[channel] = [];
    }
    if (payload) {
      if ("id" in payload) {
        this._subscriptions[channel].push(payload["id"]);
      }
      this.sendMessage({
        action: "subscribe",
        payload: {
          ...payload,
          channel,
        },
      });
    }
  }

  unsubscribe({ channel } = {}) {
    delete this._subscriptions[channel];
  }

  close() {
    if (!this._connection) return;
    this._subscriptions = new Object();
    this._connection.onclose = null;
    this._connection.close();
    this._connection = null;
  }

  isEnabled() {
    return window["WebSocket"] && this._connection != null;
  }
}

export const WebSocketsPlugin = {
  install(app, opts) {
    this.app = app;
    this.opts = opts;
    this.initializeWebSockets();
  },
  initializeWebSockets() {
    if (this.app.$ws) {
      this.app.$ws.close();
    }
    const $ws = new TunnlWebSockets(this.app);
    if (this.opts.store) {
      this.opts.store.$ws = $ws;
    }

    if (this.opts.pinia) {
      // $ws is a connection helper, not reactive state. markRaw tells Pinia 4
      // to skip it in storeToRefs() instead of warning (PINIA_R1006).
      this.opts.pinia.use(() => ({
        $ws: markRaw($ws),
      }));
    }
  },
};
